import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MaterialsManager.css';

const MaterialsManager = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    material_type: '',
    subject_id: '',
    topic_id: '',
    search: '',
    access_level: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [userRole, setUserRole] = useState(null);

  // Fetch materials
  const fetchMaterials = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...filters
      });

      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) {
          params.delete(key);
        }
      });

      const response = await axios.get(`/api/materials?${params}`);
      
      setMaterials(response.data.materials);
      setCurrentPage(response.data.pagination.current_page);
      setTotalPages(response.data.pagination.total_pages);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch materials');
      console.error('Error fetching materials:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subjects and topics for filters
  const fetchMetadata = async () => {
    try {
      const [subjectsRes, topicsRes] = await Promise.all([
        axios.get('/api/subjects'),
        axios.get('/api/topics')
      ]);
      
      setSubjects(subjectsRes.data || []);
      setTopics(topicsRes.data || []);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  // Get user role from token or context
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role_id);
      } catch (err) {
        console.error('Error parsing token:', err);
      }
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
    fetchMetadata();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMaterials(page);
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }

    try {
      await axios.delete(`/api/materials/${materialId}`);
      fetchMaterials(currentPage);
      alert('Material deleted successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete material');
    }
  };

  const handleView = async (material) => {
    try {
      const response = await axios.get(`/api/materials/${material.id}`);
      const materialWithUrl = response.data;
      
      // Open material in new tab
      window.open(materialWithUrl.signed_url, '_blank');
      
      // Track view
      await axios.post(`/api/materials/${material.id}/view`, {
        view_duration: 0,
        view_percentage: 0
      });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to access material');
    }
  };

  const getMaterialTypeIcon = (type) => {
    switch (type) {
      case 'books':
        return '📚';
      case 'handwritten_notes':
        return '📝';
      case 'lectures':
        return '🎥';
      default:
        return '📄';
    }
  };

  const getAccessLevelBadge = (level) => {
    const badges = {
      public: { class: 'badge-success', text: 'Public' },
      premium: { class: 'badge-warning', text: 'Premium' },
      restricted: { class: 'badge-danger', text: 'Restricted' }
    };
    
    const badge = badges[level] || badges.public;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const canManageMaterials = userRole === 1 || userRole === 2; // Super Admin or Admin

  return (
    <div className="materials-manager">
      <div className="materials-header">
        <h1>📚 Materials Library</h1>
        {canManageMaterials && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ Add Material
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="materials-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search materials..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select
              value={filters.material_type}
              onChange={(e) => handleFilterChange('material_type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="books">📚 Books</option>
              <option value="handwritten_notes">📝 Handwritten Notes</option>
              <option value="lectures">🎥 Lectures</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Subject:</label>
            <select
              value={filters.subject_id}
              onChange={(e) => handleFilterChange('subject_id', e.target.value)}
            >
              <option value="">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Access Level:</label>
            <select
              value={filters.access_level}
              onChange={(e) => handleFilterChange('access_level', e.target.value)}
            >
              <option value="">All Levels</option>
              <option value="public">Public</option>
              <option value="premium">Premium</option>
              <option value="restricted">Restricted</option>
            </select>
          </div>
        </div>
      </div>

      {/* Materials List */}
      {loading ? (
        <div className="loading">Loading materials...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="materials-grid">
            {materials.map(material => (
              <div key={material.id} className="material-card">
                <div className="material-header">
                  <span className="material-type">
                    {getMaterialTypeIcon(material.material_type)}
                  </span>
                  <div className="material-title">
                    <h3>{material.title}</h3>
                    {getAccessLevelBadge(material.access_level)}
                  </div>
                </div>

                <div className="material-info">
                  {material.description && (
                    <p className="material-description">{material.description}</p>
                  )}
                  
                  <div className="material-meta">
                    {material.subject && (
                      <span className="meta-item">
                        📖 {material.subject.name}
                      </span>
                    )}
                    
                    {material.topic && (
                      <span className="meta-item">
                        🏷️ {material.topic.name}
                      </span>
                    )}
                    
                    {material.file_size && (
                      <span className="meta-item">
                        📁 {formatFileSize(material.file_size)}
                      </span>
                    )}
                    
                    {material.duration && (
                      <span className="meta-item">
                        ⏱️ {formatDuration(material.duration)}
                      </span>
                    )}
                    
                    <span className="meta-item">
                      👁️ {material.view_count} views
                    </span>
                  </div>

                  <div className="material-creator">
                    Created by: {material.creator?.name} • {new Date(material.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="material-actions">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => handleView(material)}
                  >
                    📖 View
                  </button>
                  
                  {canManageMaterials && (
                    <>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedMaterial(material)}
                      >
                        ✏️ Edit
                      </button>
                      
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(material.id)}
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                ← Previous
              </button>
              
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Material Modal */}
      {(showCreateModal || selectedMaterial) && (
        <MaterialModal
          material={selectedMaterial}
          subjects={subjects}
          topics={topics}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedMaterial(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedMaterial(null);
            fetchMaterials(currentPage);
          }}
        />
      )}
    </div>
  );
};

// Material Create/Edit Modal Component
const MaterialModal = ({ material, subjects, topics, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: material?.title || '',
    description: material?.description || '',
    material_type: material?.material_type || 'books',
    subject_id: material?.subject_id || '',
    topic_id: material?.topic_id || '',
    access_level: material?.access_level || 'public',
    file_format: material?.file_format || '',
    duration: material?.duration || '',
    tags: material?.tags ? JSON.stringify(material.tags) : ''
  });
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        tags: formData.tags ? JSON.parse(formData.tags) : null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        subject_id: formData.subject_id || null,
        topic_id: formData.topic_id || null,
        file_size: file?.size || null,
        file_format: file?.name.split('.').pop() || formData.file_format
      };

      let response;
      if (material) {
        // Update existing material
        response = await axios.put(`/api/materials/${material.id}`, payload);
      } else {
        // Create new material
        response = await axios.post('/api/materials', payload);
      }

      // If we have a file and upload URL, upload the file
      if (file && response.data.upload_url) {
        await axios.put(response.data.upload_url, file, {
          headers: {
            'Content-Type': file.type
          }
        });
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save material');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{material ? 'Edit Material' : 'Add New Material'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error">{error}</div>}

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type *</label>
              <select
                required
                value={formData.material_type}
                onChange={(e) => setFormData(prev => ({...prev, material_type: e.target.value}))}
              >
                <option value="books">📚 Books</option>
                <option value="handwritten_notes">📝 Handwritten Notes</option>
                <option value="lectures">🎥 Lectures</option>
              </select>
            </div>

            <div className="form-group">
              <label>Access Level</label>
              <select
                value={formData.access_level}
                onChange={(e) => setFormData(prev => ({...prev, access_level: e.target.value}))}
              >
                <option value="public">Public</option>
                <option value="premium">Premium</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Subject</label>
              <select
                value={formData.subject_id}
                onChange={(e) => setFormData(prev => ({...prev, subject_id: e.target.value}))}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Topic</label>
              <select
                value={formData.topic_id}
                onChange={(e) => setFormData(prev => ({...prev, topic_id: e.target.value}))}
              >
                <option value="">Select Topic</option>
                {topics.map(topic => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.material_type === 'lectures' && (
            <div className="form-group">
              <label>Duration (seconds)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({...prev, duration: e.target.value}))}
              />
            </div>
          )}

          {!material && (
            <div className="form-group">
              <label>File *</label>
              <input
                type="file"
                required
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>
          )}

          <div className="form-group">
            <label>Tags (JSON format)</label>
            <textarea
              placeholder='{"category": "advanced", "difficulty": "hard"}'
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Saving...' : (material ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialsManager;

